import './styles.scss'

import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import CustomButton from '@/components/CustomButton'
import SmallLoadingIcon from '@/components/SmallLoadingIcon'
import RemoveCartItem from '@/pages/Cart/RemoveCartItem'
import { useAppSelector } from '@/store/hook'
import { selectIsSmallLoading } from '@/store/selectors/cartSelector'
import { isValidArray } from '@/libs/utils/array'
import { formatMaterialID } from '@/libs/utils/material-number'

import { NotificationManager } from '../Notification'

interface CartItem {
  materialNumber: string
}

interface AddQuickEntryProps {
  onAddItem: (materialNumber: string) => Promise<void>
  currentItems?: CartItem[]
  showDeleteButton?: boolean
  allowAddDuplicate?: boolean
  duplicateWarningContent?: string
}

function AddQuickEntry({
  onAddItem,
  currentItems,
  showDeleteButton = true,
  allowAddDuplicate = true,
  duplicateWarningContent,
}: AddQuickEntryProps) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState('')
  const [errorMessage, setErrorMessage] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isLoading = useAppSelector(selectIsSmallLoading)

  const handleInputValue = (string_) => {
    if (string_.length < 11) {
      if (/\d{8}/.test(string_)) {
        setErrorMessage(null)
        const formattedString = formatMaterialID(string_)
        setInputValue(formattedString)
        return formattedString
      }
      setErrorMessage(null)
      return string_
    }
    if (string_.length === 11) {
      if (/^\d\.\d{3}-\d{3}\.\d/.test(string_)) {
        setErrorMessage(null)
        return string_
      }
      setErrorMessage(t('errors.stringFormat'))
      return string_
    }
    setErrorMessage(t('errors.maxInputExceeded'))
    return null
  }

  const handleInputChange = (e) => {
    const formattedValue = handleInputValue(e.target.value.trim())
    if (formattedValue !== null) {
      setInputValue(formattedValue)
    }
  }

  const handleSubmit = useCallback(
    async (materialNumber) => {
      if (materialNumber.length !== 11 || !/^\d\.\d{3}-\d{3}\.\d/.test(materialNumber)) {
        setErrorMessage(t('errors.stringFormat'))
        return
      }

      const isProductAlreadyInCart = isValidArray(currentItems)
        && currentItems.some((item) => item.materialNumber === materialNumber)

      if (isProductAlreadyInCart && !allowAddDuplicate) {
        NotificationManager.error({ message: duplicateWarningContent })
        return
      }

      try {
        setIsSubmitting(true)
        await onAddItem(materialNumber)
        setInputValue('') // Only clear input on success
        setErrorMessage(null)
      } catch {
        // Error is already handled by onAddItem, but we keep the input
        // so user can retry or copy the material number
      } finally {
        setIsSubmitting(false)
      }
    },
    [t, currentItems, onAddItem, duplicateWarningContent, allowAddDuplicate],
  )

  return (
    <div className="quick-entry__wrapper">
      <div className="quick-entry__input__wrapper">
        <div className="quick-entry__left">
          <span className="quick-entry__title">{t('cart.addQuickEntryBtn')}</span>
          <input
            className="custom-input"
            data-testid="AddQuickEntry"
            disabled={isLoading || isSubmitting}
            id="AddQuickEntry"
            placeholder={t('cart.addQuickEntryPlaceHolder')}
            value={inputValue}
            onChange={handleInputChange}
            autoComplete="off"
            autoFocus
          />
        </div>

        <div className="quick-entry__button__wrapper">
          <CustomButton
            className="quick-entry__button"
            type="ghost"
            onClick={async () => handleSubmit(inputValue)}
            disabled={isLoading || isSubmitting || !inputValue}
          >
            {isLoading || isSubmitting ? <SmallLoadingIcon /> : t('cart.buttons.addArticle')}
          </CustomButton>
          {showDeleteButton && (
            <div className="quick-entry__button--remove-btn">
              <RemoveCartItem materialNumber={null} />
            </div>
          )}
        </div>
      </div>
      {errorMessage && <p className="quick-entry__input__wrapper__message">{errorMessage}</p>}
    </div>
  )
}

export default AddQuickEntry
